import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Film, X, ChevronLeft, ChevronRight,
  Upload, Play, Pause, Eye,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const frameSrc = (path) => (path?.startsWith("http") ? path : `${API_BASE}${path}`);

/**
 * Punch Cue Animation Maker.
 *
 * The coach uploads an ordered set of frame images (a flip-book) and maps it to one or
 * more Ronin cues. When a mapped cue fires during a round, the runner plays this animation
 * so the user sees a visual of the combo. Frames are stored as relative "/api/uploads/{id}"
 * paths so both the web + mobile runners can render them.
 */
export default function RoninAnimations() {
  const [animations, setAnimations] = useState([]);
  const [cues, setCues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null); // { frames, frameMs, loop, cueLabel }

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, cuesRes] = await Promise.all([
        api.get("/admin/ronin-box/animations"),
        api.get("/admin/ronin-box/cues"),
      ]);
      setAnimations(aRes.data || []);
      setCues((cuesRes.data || []).filter((c) => c.active));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  const cueById = useMemo(() => {
    const m = {};
    for (const c of cues) m[c.id] = c;
    return m;
  }, [cues]);

  const toggleActive = async (anim) => {
    try {
      await api.patch(`/admin/ronin-box/animations/${anim.id}`, { active: !anim.active });
      setAnimations((arr) => arr.map((a) => (a.id === anim.id ? { ...a, active: !anim.active } : a)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Toggle failed");
    }
  };

  const remove = async (anim) => {
    if (!window.confirm(`Delete animation "${anim.name}"? Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/ronin-box/animations/${anim.id}`);
      setAnimations((arr) => arr.filter((a) => a.id !== anim.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/ronin-box" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-ronin-admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
            <h1 className="font-display text-2xl uppercase tracking-tight">Cue Animations</h1>
          </div>
        </div>
        <Button onClick={() => setEditing({ name: "", cue_ids: [], frames: [], frame_ms: 500, loop: true, active: true })} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="animations-new">
          <Plus className="h-4 w-4 mr-2" /> New animation
        </Button>
      </div>

      <div className="p-6 max-w-5xl mx-auto">
        <p className="text-sm text-zinc-400 mb-5 max-w-2xl">
          Build flip-book animations from your own frame images and map them to cues. When a mapped cue fires during a round, the <span className="text-zinc-200 font-bold">Ronin app</span> plays your animation so the user sees the combo. Upload 2+ frames, set the speed, and assign the cues.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : animations.length === 0 ? (
          <div className="text-center py-16 border border-[#27272A] border-dashed rounded-md bg-[#0A0A0A]">
            <Film className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 mb-1">No animations yet</p>
            <p className="text-[11px] text-zinc-600 mb-4">Create one to give your cues a visual guide during rounds.</p>
            <Button onClick={() => setEditing({ name: "", cue_ids: [], frames: [], frame_ms: 500, loop: true, active: true })} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white uppercase tracking-widest font-bold text-xs">
              <Plus className="h-4 w-4 mr-2" /> Create one
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="animations-list">
            {animations.map((a) => (
              <div key={a.id} className="border border-[#27272A] rounded-md bg-[#0A0A0A] hover:border-[#3F3F46] transition-colors overflow-hidden" data-testid={`animations-row-${a.id}`}>
                <div className="flex">
                  <div className="w-28 h-28 bg-black shrink-0 border-r border-[#27272A]">
                    <FramePlayer frames={a.frames} frameMs={a.frame_ms} loop={a.loop} autoplay />
                  </div>
                  <div className="p-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white truncate">{a.name}</h3>
                      <span className={`text-[9px] px-2 py-0.5 rounded-sm uppercase tracking-widest font-bold ${a.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/30 text-zinc-500"}`}>
                        {a.active ? "Active" : "Off"}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-500 uppercase tracking-widest">{(a.frames || []).length} frames · {a.frame_ms}ms</p>
                    <p className="text-sm text-zinc-400 mt-1 truncate">
                      {(a.cue_ids || []).map((id) => cueById[id]?.text || "[missing]").join(", ") || <span className="text-amber-400">No cues assigned</span>}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <button onClick={() => toggleActive(a)} className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 text-zinc-400 hover:text-white" data-testid={`animations-toggle-${a.id}`}>
                        {a.active ? "Disable" : "Enable"}
                      </button>
                      <button
                        onClick={() => setPreview({ frames: a.frames, frameMs: a.frame_ms, loop: a.loop, cueLabel: cueById[(a.cue_ids || [])[0]]?.text || a.name })}
                        className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 text-zinc-400 hover:text-white inline-flex items-center gap-1"
                        data-testid={`animations-preview-${a.id}`}
                        title="See how it looks mid-workout"
                      >
                        <Eye className="h-3 w-3" /> Preview
                      </button>
                      <Button variant="outline" onClick={() => setEditing(a)} className="border-[#27272A] bg-transparent text-zinc-300 h-7 px-3 text-xs uppercase tracking-widest font-bold hover:text-white" data-testid={`animations-edit-${a.id}`}>
                        Edit
                      </Button>
                      <button onClick={() => remove(a)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`animations-delete-${a.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <AnimationEditor
          animation={editing}
          cues={cues}
          onPreview={(p) => setPreview(p)}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setAnimations((arr) => {
              const i = arr.findIndex((a) => a.id === saved.id);
              if (i >= 0) { const next = [...arr]; next[i] = saved; return next; }
              return [...arr, saved].sort((a, b) => a.name.localeCompare(b.name));
            });
            setEditing(null);
          }}
        />
      )}

      {preview && <WorkoutPreviewModal {...preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

/** Fullscreen overlay that mimics the live workout runner so the coach sees exactly how a
 * cue animation looks mid-round: dark screen, BOX phase badge, big flip-book, cue label. */
function WorkoutPreviewModal({ frames = [], frameMs = 500, loop = true, cueLabel = "Combo", onClose }) {
  return (
    <div className="fixed inset-0 z-[130] bg-black text-white flex flex-col items-center justify-center p-6" data-testid="animation-preview-modal">
      <button onClick={onClose} className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center text-zinc-400 hover:text-[#FF331F]" data-testid="animation-preview-close">
        <X className="h-6 w-6" />
      </button>
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, #FF331F 0%, transparent 70%)" }} />
      <div className="mb-6 px-5 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.3em] font-bold" style={{ background: "#FF331F", color: "#000" }}>
        Box
      </div>
      <div className="h-56 sm:h-72 flex items-center justify-center">
        <div className="h-full aspect-square">
          <FramePlayer frames={frames} frameMs={frameMs} loop={loop} playing />
        </div>
      </div>
      <div className="mt-8 font-display text-4xl sm:text-6xl uppercase tracking-tight text-center px-4" style={{ color: "#FF331F" }} data-testid="animation-preview-cue">
        {cueLabel}
      </div>
      <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-bold">Preview · this is how it looks during a round</p>
    </div>
  );
}

/** Loops through frame images at frameMs. Renders a placeholder when no frames. */
function FramePlayer({ frames = [], frameMs = 500, loop = true, autoplay = false, playing: playingProp }) {
  const [idx, setIdx] = useState(0);
  const [internalPlaying, setInternalPlaying] = useState(autoplay);
  const playing = playingProp != null ? playingProp : internalPlaying;

  useEffect(() => { setIdx(0); }, [frames]);

  useEffect(() => {
    if (!playing || frames.length < 2) return;
    const id = setInterval(() => {
      setIdx((i) => {
        const next = i + 1;
        if (next >= frames.length) return loop ? 0 : i;
        return next;
      });
    }, Math.max(60, frameMs));
    return () => clearInterval(id);
  }, [playing, frames, frameMs, loop]);

  if (!frames || frames.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-zinc-700">
        <Film className="h-6 w-6" />
      </div>
    );
  }
  return (
    <div className="w-full h-full relative">
      <img src={frameSrc(frames[Math.min(idx, frames.length - 1)])} alt="frame" className="w-full h-full object-contain" crossOrigin="anonymous" />
      {playingProp == null && (
        <button
          type="button"
          onClick={() => setInternalPlaying((p) => !p)}
          className="absolute bottom-1 right-1 bg-black/60 text-white p-1 rounded-sm hover:bg-black/80"
          data-testid="frameplayer-toggle"
        >
          {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

function AnimationEditor({ animation, cues, onClose, onSaved, onPreview }) {
  const isNew = !animation.id;
  const [name, setName] = useState(animation.name || "");
  const [active, setActive] = useState(animation.active !== false);
  const [loop, setLoop] = useState(animation.loop !== false);
  const [frameMs, setFrameMs] = useState(animation.frame_ms || 500);
  const [frames, setFrames] = useState([...(animation.frames || [])]);
  const [cueIds, setCueIds] = useState([...(animation.cue_ids || [])]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    for (const f of Array.from(files)) {
      if (!f.type?.startsWith("image/")) { toast.error(`${f.name}: not an image`); continue; }
      if (f.size > 12 * 1024 * 1024) { toast.error(`${f.name}: too large (max 12 MB)`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url); // store RELATIVE "/api/uploads/{id}" path
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "upload failed"}`);
      }
    }
    if (added.length) { setFrames((arr) => [...arr, ...added]); toast.success(`${added.length} frame(s) added`); }
    setUploading(false);
  };

  const removeFrame = (idx) => setFrames((arr) => arr.filter((_, i) => i !== idx));
  const moveFrame = (idx, dir) => {
    setFrames((arr) => {
      const next = [...arr];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const toggleCue = (id) => setCueIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    if (frames.length === 0) { toast.error("Upload at least one frame"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), cue_ids: cueIds, frames, frame_ms: Number(frameMs), loop, active };
      const { data } = isNew
        ? await api.post("/admin/ronin-box/animations", payload)
        : await api.patch(`/admin/ronin-box/animations/${animation.id}`, payload);
      onSaved(data);
      toast.success(isNew ? "Animation created" : "Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const groupedCues = useMemo(() => {
    const m = {};
    for (const c of cues) (m[c.category] = m[c.category] || []).push(c);
    return m;
  }, [cues]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-tight">
            {isNew ? "New animation" : `Edit · ${animation.name}`}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Upload ordered frame images, set the speed, and assign the cues that trigger this animation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Jab-Cross combo"'
              className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1"
              data-testid="animation-form-name" />
          </div>

          {/* Live preview */}
          <div className="flex gap-4 items-center">
            <div className="w-32 h-32 bg-black border border-[#27272A] rounded-sm shrink-0">
              <FramePlayer frames={frames} frameMs={frameMs} loop={loop} playing />
            </div>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Frame speed · {frameMs}ms each</label>
              <input type="range" min={80} max={1500} step={20} value={frameMs}
                onChange={(e) => setFrameMs(Number(e.target.value))}
                className="w-full accent-[#FF331F] mt-2" data-testid="animation-frame-ms" />
              <div className="flex items-center gap-2 mt-3">
                <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} className="accent-[#FF331F]" data-testid="animation-loop" />
                <label className="text-xs uppercase tracking-widest font-bold text-zinc-300">Loop</label>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => onPreview?.({
                  frames,
                  frameMs,
                  loop,
                  cueLabel: (cues.find((c) => c.id === cueIds[0])?.text) || name || "Combo",
                })}
                disabled={frames.length === 0}
                className="border-[#27272A] bg-[#050505] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest text-xs mt-3 disabled:opacity-40"
                data-testid="animation-editor-preview"
              >
                <Eye className="h-4 w-4 mr-2" /> Preview on workout screen
              </Button>
            </div>
          </div>

          {/* Frames uploader */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Frames · in order</label>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden
              onChange={(e) => uploadFiles(e.target.files)} data-testid="animation-frames-input" />
            {frames.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2" data-testid="animation-frames-grid">
                {frames.map((src, i) => (
                  <div key={`${src}-${i}`} className="relative aspect-square border border-[#27272A] bg-black rounded-sm overflow-hidden group" data-testid={`animation-frame-${i}`}>
                    <img src={frameSrc(src)} alt={`frame ${i + 1}`} className="w-full h-full object-contain" crossOrigin="anonymous" />
                    <span className="absolute top-0.5 left-0.5 bg-black/70 text-[9px] px-1 rounded-sm text-zinc-300 font-bold">{i + 1}</span>
                    <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => moveFrame(i, -1)} disabled={i === 0} className="p-1 text-zinc-300 hover:text-white disabled:opacity-30" data-testid={`animation-frame-left-${i}`}><ChevronLeft className="h-3 w-3" /></button>
                      <button onClick={() => removeFrame(i)} className="p-1 text-zinc-300 hover:text-red-400" data-testid={`animation-frame-remove-${i}`}><X className="h-3 w-3" /></button>
                      <button onClick={() => moveFrame(i, +1)} disabled={i === frames.length - 1} className="p-1 text-zinc-300 hover:text-white disabled:opacity-30" data-testid={`animation-frame-right-${i}`}><ChevronRight className="h-3 w-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              variant="outline" className="border-[#27272A] bg-[#050505] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest text-xs mt-3" data-testid="animation-add-frames">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? "Uploading…" : "Add frames"}
            </Button>
            <p className="text-[10px] text-zinc-600 mt-2">Upload 2+ images (JPG/PNG). They play in this order. Tip: transparent PNGs look best over the dark workout screen.</p>
          </div>

          {/* Cue assignment */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Trigger cues · {cueIds.length} selected</label>
            <div className="mt-2 max-h-52 overflow-y-auto border border-[#27272A] rounded-sm bg-[#050505] p-2 space-y-2" data-testid="animation-cue-picker">
              {Object.entries(groupedCues).map(([cat, list]) => (
                <div key={cat}>
                  <div className="px-1 py-1 text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{cat}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((c) => {
                      const on = cueIds.includes(c.id);
                      return (
                        <button key={c.id} type="button" onClick={() => toggleCue(c.id)}
                          className={`text-xs px-2 py-1 rounded-sm border transition-colors ${on ? "border-[#FF331F] bg-[#FF331F]/15 text-white" : "border-[#27272A] bg-[#0F0F0F] text-zinc-400 hover:text-white"}`}
                          data-testid={`animation-cue-${c.id}`}>
                          {c.text}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">This animation plays whenever one of these cues fires during a round.</p>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#FF331F]" data-testid="animation-form-active" />
            <label className="text-xs uppercase tracking-widest font-bold text-zinc-300">Active</label>
            <span className="text-[10px] text-zinc-600">— disabled animations won&apos;t play in workouts</span>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
            <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-transparent text-zinc-300 uppercase tracking-widest font-bold text-xs" data-testid="animation-form-cancel">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="animation-form-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
